import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteRoleMembers } from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import SharedDialog from '../../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import { isAbleToSeeUIElement } from '../../../../../utils/roles/client-roles';
import { RoleMember, UserRole } from '../../../../../../types';
import { AdministrationRoles } from '../../../../../utils/roles/client-roles';
import { Messages } from '../../../../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteRoleMembersDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  memberList: RoleMember[];
  changeLogRoles: string[];
  selectedRole: UserRole;
}

export default function DeleteRoleMembersDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
  memberList,
  changeLogRoles,
  selectedRole,
}: DeleteRoleMembersDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteRoleMembers(
        selectedMembers.map(({ userEmail, role, isAppAdmin, memberType }) => ({
          userEmail,
          role,
          isAppAdmin,
          ...(memberType && { memberType }),
        })),
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted('Member(s)'),
      });
      queryClient.setQueryData(
        [UserKeys.RoleMembers, selectedRole.roleName],
        (oldData: RoleMember[]) =>
          oldData.filter((member) => !selectedRows.includes(member.userEmail))
      );
      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          'the selected member(s) from the role'
        ),
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.role_details.delete_role_member, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.roleManager,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.RemoveMemberFromRole.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const selectedMembers =
    memberList.filter((member) => selectedRows.includes(member.userEmail)) ||
    [];

  const isBulk = selectedMembers.length > 1;

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={isBulk ? 'Remove Members' : 'Remove Member'}
      dialogContent={
        <Box>
          <Typography>
            {isBulk ? (
              <>
                Remove{' '}
                <strong>{selectedMembers.length} selected members</strong> from
                this role?
              </>
            ) : (
              <>
                Remove <strong>{selectedMembers[0]?.userEmail}</strong> from
                this role?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: isBulk ? 'Remove Members' : 'Remove Member',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
